import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@wds/box';
import { Button } from '@wds/button';
import { IconArrowRefresh, IconImage, IconXCircle } from '@wds/icons';
import { Typography } from '@wds/typography';
import { Toast } from '@wds/toast';

import { getCookie } from 'sma.common/utils';

import PropTypes from 'prop-types';
import STRINGS from './constants';

const ImageUpload = (props) => {
	const {
		label, id, allowedFormats, acceptType, url,
	} = props;
	const [state, setState] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [base64String, setBase64String] = useState('');
	const [toastInfo, setToastInfo] = useState({ title: null, text: null, show: false, color: null });

	const firstMount = useRef(true);

	const handleUploadSuccess = () => {
		setToastInfo({ show: true, color: 'success', title: STRINGS.success, text: STRINGS.fileUploaded });
	};

	const handleUploadFailure = (error) => {
		setToastInfo({
			show: true,
			color: 'error',
			title: STRINGS.error,
			text: error ? STRINGS[error] : STRINGS.fileUploadError,
		});
	};

	const handleFileRemoved = () => {
		setToastInfo({ show: true, color: 'success', title: STRINGS.success, text: STRINGS.removed });
	};

	const onChange = (fieldId) => (event) => {
		const file = event.target.files[0];
		const reader = new FileReader();
		reader.onload = () => {
			const base64 = reader.result;
			setState({
				...state,
				[fieldId]: base64,
			});
		};
		reader.readAsDataURL(file);
	};

	const handleFileRequest = async (file, method) => {
		setIsLoading(true);
		let status;
		let fileUrl;
		if (url !== undefined) {
			fileUrl = url;
		} else {
			fileUrl = reverse(`api-smapply-${id}`);
		}
		const request = {
			method,
			headers: {
				Accept: acceptType,
				'Content-Type': file ? 'image/png;base64' : undefined,
				'X-CSRFToken': getCookie('csrftoken'),
			},
			body: file ? state[id] : undefined,
		};

		try {
			const response = await fetch(fileUrl, request);
			status = response.status;
			if (method === 'GET') {
				if (status === 200) {
					const blob = await response.blob();
					const reader = new FileReader();
					reader.onload = () => {
						setBase64String(reader.result);
					};
					reader.readAsDataURL(blob);
				}
			} else if (method === 'PUT' && (status === 200 || status === 201)) {
				handleUploadSuccess();
			} else if (method === 'DELETE' && status === 204) {
				handleFileRemoved();
			} else {
				const errorData = await response.json();
				handleUploadFailure(errorData[id]);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			handleUploadFailure();
		} finally {
			setIsLoading(false);
		}
	};

	const handleRemoveImage = () => {
		setState({
			...state,
			[id]: null,
		});
	};

	useEffect(() => {
		if (firstMount.current) {
			firstMount.current = false;
			setBase64String(state[id]);
			return;
		}
		if (state[id]) {
			handleFileRequest(state[id], 'PUT');
			setBase64String(state[id]);
		} else {
			setBase64String(null);
			handleFileRequest(null, 'DELETE');
		}
	}, [state]);

	useEffect(() => {
		handleFileRequest(null, 'GET');
	}, []);

	return (
		<Box style={{ maxWidth: 102 }}>
			{isLoading && (
				<Box
					display='flex'
					justifyContent='center'
					alignItems='center'
					style={{
						width: '100px',
						height: '100px',
					}}
				/>
			)}
			{!isLoading && (
				<>
					<Box
						display='flex'
						flexDirection='column'
						justifyContent='space-around'
					>
						<Box>
							<Typography variant='bodySm' weight='medium'>
								{label}
							</Typography>
						</Box>
						<Box
							className='image-container'
							id={`sma-${id}-image`}
							display='flex'
							justifyContent='center'
							alignItems='center'
							p={1}
							style={{
								width: '100px',
								height: '100px',
								border: '1px solid #D0D2D3',
								backgroundColor: '#EDEEEE',
							}}
						>
							<Box
								display='flex'
								justifyContent='space-around'
								alignItems='center'
								style={{
									width: '92px',
									height: '92px',
								}}
							>
								<Box>
									{!base64String && (<IconImage size='lg' />)}
									{base64String && (
										<img
											src={base64String}
											alt='Preview'
											style={{ maxHeight: '92px', maxWidth: '92px' }}
										/>
									)}
								</Box>
							</Box>
						</Box>
						<Box
							className='image-upload-controls'
							my={2}
						>
							<Box
								display='flex'
								justifyContent='space-between'
							>
								{!base64String && (
									<Box>
										<Button
											variant='solid'
											color='primary'
											size='sm'
											onClick={() => document.getElementById(`fileInput-${id}`).click()}
										>
											{STRINGS.chooseFile}
										</Button>
									</Box>
								)}

								{base64String && (
									<>
										<Button
											variant='solid'
											id={`sma-${id}-change`}
											color='primary'
											size='sm'
											onClick={() => document.getElementById(`fileInput-${id}`).click()}
											label='Change'
										>
											<IconArrowRefresh size='lg' aria-label={STRINGS.labels.replace} />
										</Button>
										<Button
											variant='solid'
											id={`sma-${id}-remove`}
											color='warning'
											size='sm'
											onClick={handleRemoveImage}
											label='Remove'
										>
											<IconXCircle size='lg' aria-label={STRINGS.labels.remove} />
										</Button>
									</>
								)}
							</Box>
							<Box style={{ maxWidth: '100px' }}>
								<Typography
									variant='caption'
								>
									{STRINGS.accepts}
									:
									{' '}
									{allowedFormats}
								</Typography>
							</Box>
						</Box>
					</Box>
					<Toast
						autoClose
						color={toastInfo.color}
						dismissible
						onClose={() => setToastInfo({
							...toastInfo,
							title: null,
							text: null,
							show: false,
						})}
						show={toastInfo.show}
					>
						<Toast.Title>
							{toastInfo.title}
						</Toast.Title>
						<Toast.Body>
							{toastInfo.text}
						</Toast.Body>
					</Toast>
				</>
			)}
			<input
				type='file'
				id={`fileInput-${id}`}
				style={{ display: 'none' }}
				onChange={onChange(id)}
				maxLength={20}
			/>
		</Box>
	);
};

ImageUpload.propTypes = {
	label: PropTypes.string.isRequired,
	id: PropTypes.string.isRequired,
	allowedFormats: PropTypes.string,
	acceptType: PropTypes.string,
	url: PropTypes.string,
};

ImageUpload.defaultProps = {
	allowedFormats: 'jpg, jpeg, png',
	acceptType: 'image/png, image/jpeg',
	url: undefined,
};

export default ImageUpload;
